import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { MessageCircle, Save, Loader2, PlayCircle, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getAdminMessagingConfig, updateAdminMessagingConfig, testAdminMessagingConfig } from '../../../lib/api';

// Provider registry — labels + per-provider field visibility
const PROVIDERS = [
  { value: 'mock',       label: 'Mock (test)' },
  { value: 'meta_cloud', label: 'Meta / WhatsApp Cloud API' },
  { value: 'twilio',     label: 'Twilio (WhatsApp + SMS)' },
  { value: 'msg91',      label: 'MSG91 (SMS)' },
  { value: 'gupshup',    label: 'Gupshup (WhatsApp + SMS)' },
];

const FIELD_LABELS = {
  meta_cloud: { api_key: 'App ID (optional)',        api_secret: 'Access Token',          sender: 'Sender / WABA Number' },
  twilio:     { api_key: 'Account SID',              api_secret: 'Auth Token',            sender: 'From (WhatsApp/SMS)' },
  msg91:      { api_key: 'Account ID (optional)',    api_secret: 'Auth Key',              sender: 'Sender ID' },
  gupshup:    { api_key: 'API Key',                  api_secret: 'App Password',          sender: 'Sender Name' },
  mock:       { api_key: 'API Key',                  api_secret: 'API Secret',            sender: 'Sender' },
};

const FIELD = ({ label, children, hint }) => (
  <div>
    <label className="text-[11px] font-bold tracking-widest text-zinc-400 uppercase mb-1.5 block">{label}</label>
    {children}
    {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
  </div>
);

export default function MessagingProviders() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState(null);
  const [masked, setMasked] = useState({ api_key: '', api_secret: '' });
  const [configuredWA, setConfiguredWA] = useState(false);
  const [configuredSMS, setConfiguredSMS] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testKind, setTestKind] = useState('whatsapp');
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await getAdminMessagingConfig();
      setForm({
        enabled: !!d.enabled,
        provider: d.provider || 'meta_cloud',
        api_base_url: d.api_base_url || 'https://graph.facebook.com',
        api_key: '',
        api_secret: '',
        sender: d.sender || '',
        phone_number_id: d.phone_number_id || '',
        waba_id: d.waba_id || '',
        business_id: d.business_id || '',
        template_name: d.template_name || '',
        template_lang: d.template_lang || 'en_GB',
        template_has_url_button: !!d.template_has_url_button,
        otp_template: d.otp_template || '',
        order_confirmation_template: d.order_confirmation_template || '',
      });
      setMasked({ api_key: d.api_key_masked || '', api_secret: d.api_secret_masked || '' });
      setConfiguredWA(!!d.configured_whatsapp);
      setConfiguredSMS(!!d.configured_sms);
    } catch (e) {
      toast.error('Failed to load messaging config');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const d = await updateAdminMessagingConfig(form);
      setMasked({ api_key: d.api_key_masked || '', api_secret: d.api_secret_masked || '' });
      setConfiguredWA(!!d.configured_whatsapp);
      setConfiguredSMS(!!d.configured_sms);
      setForm((f) => ({ ...f, api_key: '', api_secret: '' }));
      toast.success('Messaging settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    const to = testTo.replace(/\D/g, '');
    if (to.length < 8) return toast.error('Enter a full international number (with country code)');
    setTesting(true);
    setTestResult(null);
    try {
      const r = await testAdminMessagingConfig({ to, kind: testKind });
      setTestResult(r);
      if (r.ok) toast.success(`Test ${testKind.toUpperCase()} sent via ${r.provider}`);
      else toast.error(`Failed: ${r.message}`);
    } catch (e) {
      setTestResult({ ok: false, message: e?.response?.data?.detail || 'Test failed' });
      toast.error('Test failed');
    } finally { setTesting(false); }
  };

  const provider = form?.provider || 'meta_cloud';
  const labels = FIELD_LABELS[provider] || FIELD_LABELS.mock;
  const showMetaGroup = provider === 'meta_cloud';
  const isMock = provider === 'mock';

  if (loading || !form) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-6 flex items-center gap-2 text-zinc-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Loading messaging config…
        </CardContent>
      </Card>
    );
  }

  const inputCls = "bg-zinc-950 border-zinc-800 text-zinc-100 font-mono h-11";

  return (
    <Card className="bg-zinc-900 border-zinc-800" data-testid="messaging-providers">
      <CardHeader className="border-b border-zinc-800/70 pb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <CardTitle className="text-zinc-100 flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-md bg-emerald-500/15 text-emerald-300 flex items-center justify-center border border-emerald-700/40">
              <MessageCircle size={18} />
            </span>
            WhatsApp / SMS Provider
          </CardTitle>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 select-none">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => upd({ enabled: e.target.checked })}
                className="w-4 h-4 accent-emerald-500"
                data-testid="msg-enabled-toggle"
              />
              <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-300">Enabled</span>
            </label>
            <select
              value={provider}
              onChange={(e) => upd({ provider: e.target.value })}
              className="h-9 rounded-md bg-zinc-950 border border-zinc-800 text-zinc-100 px-3 text-xs font-bold uppercase tracking-widest"
              data-testid="msg-provider-select"
            >
              {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-2 flex-wrap">
          <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${configuredWA ? 'bg-emerald-500/20 text-emerald-300 border-emerald-700/40' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>WhatsApp {configuredWA ? 'live' : 'off'}</span>
          <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${configuredSMS ? 'bg-emerald-500/20 text-emerald-300 border-emerald-700/40' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>SMS {configuredSMS ? 'live' : 'off'}</span>
        </div>
      </CardHeader>

      <CardContent className="space-y-5 pt-5">
        {/* Row 1 — Api key / secret / sender */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FIELD label={labels.api_key}>
            <Input value={form.api_key} onChange={(e) => upd({ api_key: e.target.value })} placeholder={masked.api_key || 'e.g. ACxxxxxxx'} className={inputCls} data-testid="msg-api-key" />
          </FIELD>
          <FIELD label={labels.api_secret} hint="Leave blank to keep. Type __CLEAR__ to remove.">
            <Input value={form.api_secret} onChange={(e) => upd({ api_secret: e.target.value })} placeholder={masked.api_secret || '•••••'} className={inputCls} data-testid="msg-api-secret" />
          </FIELD>
          <FIELD label={labels.sender}>
            <Input value={form.sender} onChange={(e) => upd({ sender: e.target.value })} placeholder="+15555550100" className={inputCls} data-testid="msg-sender" />
          </FIELD>
        </div>

        {/* Row 2 — Meta-only IDs */}
        {showMetaGroup && (
          <FIELD label="API URL (Graph API or BSP proxy)" hint="Default: https://graph.facebook.com — change only if your provider gives a custom API URL.">
            <Input value={form.api_base_url} onChange={(e) => upd({ api_base_url: e.target.value })} placeholder="https://graph.facebook.com" className={inputCls} data-testid="msg-api-base-url" />
          </FIELD>
        )}
        {showMetaGroup && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FIELD label="Phone Number ID (Meta)">
              <Input value={form.phone_number_id} onChange={(e) => upd({ phone_number_id: e.target.value })} placeholder="778288702040052" className={inputCls} data-testid="msg-phone-id" />
            </FIELD>
            <FIELD label="WABA ID">
              <Input value={form.waba_id} onChange={(e) => upd({ waba_id: e.target.value })} placeholder="4804863843074154" className={inputCls} data-testid="msg-waba-id" />
            </FIELD>
            <FIELD label="Business ID">
              <Input value={form.business_id} onChange={(e) => upd({ business_id: e.target.value })} placeholder="1178251134294811" className={inputCls} data-testid="msg-business-id" />
            </FIELD>
          </div>
        )}

        {/* Row 3 — Template name / lang / has-url-button */}
        {(showMetaGroup || provider === 'msg91') && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FIELD label={provider === 'msg91' ? 'Template ID' : 'Approved Template Name'}>
              <Input value={form.template_name} onChange={(e) => upd({ template_name: e.target.value })} placeholder={provider === 'msg91' ? '63b1…' : 'otp_verification'} className={inputCls} data-testid="msg-template-name" />
            </FIELD>
            {showMetaGroup && (
              <FIELD label="Template Language">
                <Input value={form.template_lang} onChange={(e) => upd({ template_lang: e.target.value })} placeholder="en_GB" className={inputCls} data-testid="msg-template-lang" />
              </FIELD>
            )}
            {showMetaGroup && (
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 select-none">
                  <input
                    type="checkbox"
                    checked={form.template_has_url_button}
                    onChange={(e) => upd({ template_has_url_button: e.target.checked })}
                    className="w-4 h-4 accent-emerald-500"
                    data-testid="msg-template-url-btn"
                  />
                  <span className="text-sm text-zinc-200">Template has URL button (also takes OTP)</span>
                </label>
              </div>
            )}
          </div>
        )}

        {/* OTP Template */}
        <FIELD label="OTP Template (use {otp})">
          <textarea
            value={form.otp_template}
            onChange={(e) => upd({ otp_template: e.target.value })}
            rows={2}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-md text-zinc-100 p-3 text-sm font-mono"
            placeholder="{otp} is your verification code."
            data-testid="msg-otp-template"
          />
        </FIELD>

        {/* Order Confirmation Template */}
        <FIELD label="Order Confirmation Template (use {name}, {order_id}, {total}, {track_url})">
          <textarea
            value={form.order_confirmation_template}
            onChange={(e) => upd({ order_confirmation_template: e.target.value })}
            rows={2}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-md text-zinc-100 p-3 text-sm font-mono"
            placeholder="Hi {name}! Your order #{order_id} confirmed. Total: {total}. Track: {track_url}"
            data-testid="msg-order-template"
          />
        </FIELD>

        <div className="text-[11px] text-zinc-500 border-t border-zinc-800/70 pt-3">
          {isMock
            ? 'Mock mode is active — OTPs are printed to backend logs and not actually sent. Switch to a live provider to send real messages.'
            : 'Until Enabled + live credentials are saved, OTPs will fail. Order confirmations log to db.notifications.'}
        </div>

        {/* Test row */}
        <div className="border border-zinc-800 rounded-lg bg-zinc-950/60 p-3 space-y-2">
          <div className="text-[11px] font-bold tracking-widest text-zinc-300 uppercase flex items-center gap-2">
            <PlayCircle size={14} /> Test OTP delivery
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="Full number e.g. 919876543210"
              className="bg-zinc-950 border-zinc-800 text-zinc-100 font-mono flex-1 min-w-[220px] h-10"
              data-testid="msg-test-to"
            />
            <select
              value={testKind}
              onChange={(e) => setTestKind(e.target.value)}
              className="h-10 rounded-md bg-zinc-950 border border-zinc-800 text-zinc-100 px-3 text-xs font-bold uppercase tracking-widest"
              data-testid="msg-test-kind"
            >
              <option value="whatsapp">WhatsApp</option>
              <option value="sms">SMS</option>
            </select>
            <Button onClick={runTest} disabled={testing} className="bg-emerald-500/90 hover:bg-emerald-500 text-black font-bold" data-testid="msg-test-btn">
              {testing ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Sending…</> : 'Send Test'}
            </Button>
          </div>
          {testResult && (
            <div className={`flex items-start gap-2 rounded-md p-2 text-sm ${testResult.ok ? 'bg-emerald-500/10 text-emerald-200 border border-emerald-700/40' : 'bg-red-500/10 text-red-200 border border-red-700/40'}`} data-testid="msg-test-result">
              {testResult.ok ? <CheckCircle2 size={16} className="mt-0.5" /> : <XCircle size={16} className="mt-0.5" />}
              <div className="min-w-0 break-words">
                <div className="font-bold">{testResult.ok ? `Sent via ${testResult.provider}` : 'Test failed'}</div>
                <div className="text-xs opacity-80 mt-0.5">{testResult.message || (testResult.ok ? `OTP: ${testResult.otp_sent}` : '')}</div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-[color:var(--gold)] text-black font-bold" data-testid="msg-save-btn">
            <Save size={14} className="mr-1.5" /> {saving ? 'Saving…' : 'Save Provider Config'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
